import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, RefreshCw, Loader2, Check, Clock, Coffee, Eye, EyeOff, Trash2, PawPrint, Utensils, ExternalLink, ChevronDown, ChevronUp, Users, Mail, Megaphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORIES = ['Treats', 'Chews', 'Doggy Ice Cream', 'Sample Tasting Board', 'Specials', 'Extras'];

const STATUS_STYLES = {
  new: 'bg-red-100 text-red-700 border-red-200',
  preparing: 'bg-amber-100 text-amber-700 border-amber-200',
  delivered: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

// ============================================================================
// Menu editor
// ============================================================================

const MenuEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/admin/teds-table/menu`, { headers: authHeaders() });
      setItems(res.data || []);
    } catch (err) {
      toast.error('Could not load menu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/teds-table/sync-shopify`, {}, { headers: authHeaders() });
      toast.success(`Synced ${res.data.count} items from Shopify`);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const patchItem = async (id, patch, silent = false) => {
    try {
      await axios.patch(`${API}/admin/teds-table/menu/${id}`, patch, { headers: authHeaders() });
      setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
      if (!silent) toast.success('Updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Remove this item from the menu?')) return;
    try {
      await axios.delete(`${API}/admin/teds-table/menu/${id}`, { headers: authHeaders() });
      setItems(prev => prev.filter(it => it.id !== id));
      toast.success('Removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const grouped = CATEGORIES.map(cat => ({
    category: cat,
    items: items.filter(it => it.category === cat),
  })).filter(g => g.items.length > 0);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#0FA49C] animate-spin" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-4">
        <div>
          <h3 className="font-bold text-[#222]">Menu items</h3>
          <p className="text-sm text-[#666]">{items.length} total • sourced from <a href="https://ultimatepetfoods.co.uk/collections/teds-table" target="_blank" rel="noreferrer" className="text-[#0FA49C] underline">Shopify collection</a></p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white"
          data-testid="teds-table-sync-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {syncing ? 'Syncing…' : 'Sync from Shopify'}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <ChefHat className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 mb-4">Menu is empty. Click &quot;Sync from Shopify&quot; to load items.</p>
        </div>
      ) : grouped.map(group => (
        <div key={group.category} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h4 className="font-bold text-sm uppercase tracking-wide text-[#222]">{group.category} ({group.items.length})</h4>
          </div>
          <div className="divide-y divide-gray-100">
            {group.items.map(item => (
              <AdminMenuRow
                key={item.id}
                item={item}
                onPatch={patchItem}
                onDelete={deleteItem}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// One row in the admin menu editor. Click the row (or the chevron) to reveal
// a preview of exactly what the customer sees on /teds-table.
const AdminMenuRow = ({ item, onPatch, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [stockDraft, setStockDraft] = useState(
    item.stock === null || item.stock === undefined ? '' : String(item.stock)
  );
  const [editingStock, setEditingStock] = useState(false);
  const hasIngredients = !!item.ingredients_html;
  const hasSuitable = Array.isArray(item.suitable_for) && item.suitable_for.length > 0;
  const hasMoreInfo = !!item.more_info_html;
  const hasAnyPreview = hasIngredients || hasSuitable || hasMoreInfo || item.short_description;
  const isSoldOut = item.available === false;
  const stockTracked = item.stock !== null && item.stock !== undefined;

  useEffect(() => {
    setStockDraft(item.stock === null || item.stock === undefined ? '' : String(item.stock));
  }, [item.stock]);

  const saveStock = async () => {
    const trimmed = stockDraft.trim();
    let payload;
    if (trimmed === '') {
      // Empty = untracked (unlimited)
      payload = { stock: -1 }; // sentinel: backend accepts int; use -1 → we'll interpret specially
      // Actually — simpler: use a dedicated /stock endpoint or send null. Let's not overload.
      // Instead: send a PATCH with stock explicitly set. Backend MenuItemUpdate treats None
      // as "no change", so we can't clear stock via PATCH. Send a large sentinel via a
      // dedicated call. For MVP, don't allow un-tracking once tracked.
      toast.error('Enter a number ≥ 0. To untrack stock, use the admin backend directly.');
      return;
    }
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Stock must be 0 or higher');
      return;
    }
    await onPatch(item.id, { stock: n });
    setEditingStock(false);
  };

  return (
    <div className="border-b border-gray-100 last:border-b-0" data-testid={`admin-menu-item-${item.id}`}>
      <div className="p-3 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setExpanded(v => !v)}
          disabled={!hasAnyPreview}
          className={`p-1 rounded ${hasAnyPreview ? 'hover:bg-gray-100 text-gray-500' : 'text-gray-300 cursor-default'}`}
          data-testid={`admin-menu-expand-${item.id}`}
          title={hasAnyPreview ? 'Preview customer view' : 'No preview available'}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {item.image_url && <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
        <div className="flex-1 min-w-[140px]">
          <p className="font-semibold text-sm truncate">{item.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-gray-500">£{item.price.toFixed(2)}</p>
            {isSoldOut && <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider">Sold Out</span>}
          </div>
        </div>

        {/* Stock control */}
        <div className="flex items-center gap-1.5" data-testid={`admin-menu-stock-wrap-${item.id}`}>
          <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Stock</span>
          {editingStock ? (
            <>
              <input
                type="number"
                min="0"
                value={stockDraft}
                onChange={(e) => setStockDraft(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') saveStock(); }}
                className="w-16 text-xs px-2 py-1 border border-gray-300 rounded"
                data-testid={`admin-menu-stock-input-${item.id}`}
              />
              <button onClick={saveStock} className="text-[#0FA49C] p-1 hover:bg-[#0FA49C]/10 rounded" data-testid={`admin-menu-stock-save-${item.id}`}>
                <Check className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditingStock(true)}
              className={`text-xs font-bold px-2 py-1 rounded border ${
                !stockTracked ? 'text-gray-400 border-gray-200 bg-gray-50' :
                item.stock === 0 ? 'text-red-600 border-red-200 bg-red-50' :
                item.stock <= 5 ? 'text-amber-700 border-amber-200 bg-amber-50' :
                'text-emerald-700 border-emerald-200 bg-emerald-50'
              }`}
              data-testid={`admin-menu-stock-${item.id}`}
              title="Click to edit stock quantity"
            >
              {!stockTracked ? '∞' : item.stock}
            </button>
          )}
        </div>

        <select
          value={item.category}
          onChange={(e) => onPatch(item.id, { category: e.target.value })}
          className="text-xs px-2 py-1 border border-gray-300 rounded"
          data-testid={`admin-menu-category-${item.id}`}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          onClick={() => onPatch(item.id, { visible: !item.visible })}
          title={item.visible ? 'Force SOLD OUT (regardless of stock)' : 'Back on menu — orderable if stock allows'}
          className={`p-1.5 rounded ${item.visible ? 'text-emerald-600 hover:bg-emerald-50' : 'text-red-500 hover:bg-red-50 bg-red-50'}`}
          data-testid={`admin-menu-visible-${item.id}`}
        >
          {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-1.5 rounded text-red-500 hover:bg-red-50"
          data-testid={`admin-menu-delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {expanded && hasAnyPreview && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden bg-gray-50 border-t border-gray-100"
          >
            <div className="px-4 py-3">
              <p className="text-[10px] uppercase tracking-widest text-[#0FA49C] font-bold mb-2">Customer preview</p>
              {item.short_description && (
                <p className="text-xs text-gray-600 mb-2 italic">&ldquo;{item.short_description}&rdquo;</p>
              )}
              <Accordion type="multiple" className="bg-white rounded-lg px-3 border border-gray-200">
                {hasIngredients && (
                  <AccordionItem value="ingredients" className="border-b-0">
                    <AccordionTrigger className="py-2 text-xs font-bold uppercase tracking-wide text-[#1f6d62] hover:no-underline">Ingredients</AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <div className="text-xs text-gray-700 [&_p]:my-1 [&_ul]:pl-4 [&_ul]:my-1 [&_li]:my-0.5" dangerouslySetInnerHTML={{ __html: item.ingredients_html }} />
                    </AccordionContent>
                  </AccordionItem>
                )}
                {hasSuitable && (
                  <AccordionItem value="suitable" className="border-b-0">
                    <AccordionTrigger className="py-2 text-xs font-bold uppercase tracking-wide text-[#1f6d62] hover:no-underline">Suitable For</AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
                        {item.suitable_for.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                )}
                {hasMoreInfo && (
                  <AccordionItem value="more" className="border-b-0">
                    <AccordionTrigger className="py-2 text-xs font-bold uppercase tracking-wide text-[#1f6d62] hover:no-underline">More Information</AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <div className="text-xs text-gray-700 [&_h4]:text-[11px] [&_h4]:font-bold [&_h4]:uppercase [&_h4]:tracking-wide [&_h4]:text-[#1a1a1a] [&_h4]:mt-3 [&_h4]:mb-1 [&_p]:my-1 [&_ul]:pl-4 [&_ul]:my-1 [&_li]:my-0.5 [&_table]:border-collapse [&_table]:my-2 [&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1" dangerouslySetInnerHTML={{ __html: item.more_info_html }} />
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
              <p className="mt-3 text-[10px] text-gray-400">Content synced from Shopify body_html — re-sync to refresh.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const OrderCard = ({ order, onStatusChange, onDelete }) => {
  const nextStatus = order.status === 'new' ? 'preparing' : order.status === 'preparing' ? 'delivered' : null;
  const nextLabel = order.status === 'new' ? 'Start preparing' : order.status === 'preparing' ? 'Mark delivered' : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
      data-testid={`admin-order-${order.id}`}
    >
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100 bg-gradient-to-r from-[#f0fdfa] to-white">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-500 font-bold">{order.order_number}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-lg font-black text-[#1f6d62]">Table {order.table_number}</span>
            <span className="text-gray-300">•</span>
            <span className="flex items-center gap-1 text-sm font-semibold text-[#222]">
              <PawPrint className="w-3.5 h-3.5" /> {order.dog_name}
            </span>
            {order.owner_first_name && <span className="text-xs text-gray-500">({order.owner_first_name})</span>}
          </div>
          {order.customer_email && (
            <a
              href={`mailto:${order.customer_email}`}
              className="inline-flex items-center gap-1 text-xs text-[#0FA49C] hover:underline mt-1"
              data-testid={`order-email-${order.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="w-3 h-3" /> {order.customer_email}
            </a>
          )}
        </div>
        <div className="text-right">
          <span className={`inline-block text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${STATUS_STYLES[order.status] || STATUS_STYLES.new}`}>
            {order.status}
          </span>
          <p className="text-[10px] text-gray-400 mt-1">{new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="space-y-1 mb-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-[#222]">{it.name} <span className="text-gray-400">×{it.quantity}</span></span>
              <span className="font-semibold text-gray-600">£{it.line_total.toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 pt-2">
          <div>
            <span className={`text-xs font-bold ${order.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>
              {order.payment_status === 'paid' ? '✓ PAID' : `Payment: ${order.payment_status}`}
            </span>
            <span className="ml-2 text-lg font-black text-[#1f6d62]">£{order.total.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2">
            {nextStatus && order.payment_status === 'paid' && (
              <Button
                size="sm"
                onClick={() => onStatusChange(order.id, nextStatus)}
                className="bg-[#1f6d62] hover:bg-[#14544b] text-white h-8"
                data-testid={`admin-order-advance-${order.id}`}
              >
                {nextStatus === 'preparing' ? <Coffee className="w-3.5 h-3.5 mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                {nextLabel}
              </Button>
            )}
            <button
              onClick={() => onDelete(order.id)}
              className="p-1.5 rounded text-red-400 hover:bg-red-50 hover:text-red-600"
              data-testid={`admin-order-delete-${order.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Web Audio API chime — a soft two-tone bell. Works without any external
// asset and doesn't require the browser to fetch anything. Only works after
// the user has interacted with the page (browser policy), which is fine for
// us since the sound toggle itself is a user gesture.
const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    const beep = (freq, start, dur, gain = 0.28) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(gain, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.connect(g);
      g.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur);
    };

    beep(880, now, 0.35);          // A5
    beep(1174.66, now + 0.18, 0.5); // D6

    // Auto-close the context after the sound finishes to release the audio device
    setTimeout(() => { ctx.close?.().catch?.(() => {}); }, 900);
  } catch { /* silent */ }
};

const CHIME_SPEEDS = [
  { value: 5000, label: 'Slow · 5s' },
  { value: 3000, label: 'Normal · 3s' },
  { value: 2000, label: 'Fast · 2s' },
  { value: 1000, label: 'Urgent · 1s' },
];

const OrdersView = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem('teds_table_sound_on') === 'true';
    } catch { return false; }
  });
  const [chimeSpeed, setChimeSpeed] = useState(() => {
    try {
      const stored = parseInt(localStorage.getItem('teds_table_chime_speed') || '2000', 10);
      return CHIME_SPEEDS.some(s => s.value === stored) ? stored : 2000;
    } catch { return 2000; }
  });

  const persistSoundOn = (v) => {
    setSoundOn(v);
    try { localStorage.setItem('teds_table_sound_on', v ? 'true' : 'false'); } catch { /* ignore */ }
    if (v) {
      try { playChime(); } catch { /* silent */ }
    }
  };

  const persistChimeSpeed = (v) => {
    setChimeSpeed(v);
    try { localStorage.setItem('teds_table_chime_speed', String(v)); } catch { /* ignore */ }
    // Play a preview so staff can hear how fast the interval will be
    try { playChime(); } catch { /* silent */ }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/teds-table/orders`, { headers: authHeaders() });
      setOrders(res.data || []);
    } catch (err) {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 8000);
    return () => clearInterval(interval);
  }, []);

  // Sound loop — chimes on the selected interval while there is at least one
  // paid order still in the 'new' status. Stops the moment staff clicks
  // "Start preparing" on the last new order (or turns the toggle off).
  const newPaidCount = orders.filter(o => o.payment_status === 'paid' && o.status === 'new').length;
  useEffect(() => {
    if (!soundOn || newPaidCount === 0) return;
    playChime();
    const t = setInterval(playChime, chimeSpeed);
    return () => clearInterval(t);
  }, [soundOn, newPaidCount, chimeSpeed]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/teds-table/orders/${orderId}`, { status }, { headers: authHeaders() });
      toast.success(`Marked ${status}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await axios.delete(`${API}/admin/teds-table/orders/${orderId}`, { headers: authHeaders() });
      setOrders(prev => prev.filter(o => o.id !== orderId));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const paidOrders = orders.filter(o => o.payment_status === 'paid');
  const active = paidOrders.filter(o => o.status !== 'delivered');
  const delivered = paidOrders.filter(o => o.status === 'delivered');

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-[#0FA49C] animate-spin" /></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-4">
        <div>
          <h3 className="font-bold text-[#222]">Live orders</h3>
          <p className="text-sm text-[#666] flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Auto-refresh every 8s • {active.length} active
            {soundOn && newPaidCount > 0 && (
              <span className="ml-2 text-amber-600 font-semibold animate-pulse">
                🔔 chiming — {newPaidCount} new order{newPaidCount === 1 ? '' : 's'}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="admin-orders-sound-label">
            <input
              type="checkbox"
              checked={soundOn}
              onChange={(e) => persistSoundOn(e.target.checked)}
              className="w-4 h-4 accent-[#0FA49C]"
              data-testid="admin-orders-sound-toggle"
            />
            <span className="text-sm text-[#666]">Sound alert</span>
          </label>
          <select
            value={chimeSpeed}
            onChange={(e) => persistChimeSpeed(parseInt(e.target.value, 10))}
            disabled={!soundOn}
            className="text-xs px-2 py-1.5 border border-gray-300 rounded bg-white disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="admin-orders-chime-speed"
            title="How often the chime repeats until you press 'Start preparing'"
          >
            {CHIME_SPEEDS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {active.length === 0 && delivered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <Utensils className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600">No orders yet. Once customers scan the QR code and pay, orders will appear here.</p>
        </div>
      ) : (
        <>
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#666] mb-2">Active ({active.length})</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <AnimatePresence>
                {active.map(o => <OrderCard key={o.id} order={o} onStatusChange={updateStatus} onDelete={deleteOrder} />)}
              </AnimatePresence>
            </div>
          </div>
          {delivered.length > 0 && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-widest text-[#666] mb-2">Delivered today ({delivered.length})</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 opacity-60">
                {delivered.slice(0, 10).map(o => <OrderCard key={o.id} order={o} onStatusChange={updateStatus} onDelete={deleteOrder} />)}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// Live customer presence counter
// ============================================================================

const PresenceCounter = () => {
  const [data, setData] = useState({ active: 0, by_step: { welcome: 0, menu: 0, review: 0 } });

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/admin/teds-table/presence`, { headers: authHeaders() });
        if (!cancelled) setData(res.data);
      } catch { /* silent */ }
    };
    fetchIt();
    const t = setInterval(fetchIt, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="teds-table-presence">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Live on the menu</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-4xl font-black text-[#0FA49C]" data-testid="teds-table-presence-count">{data.active}</p>
            <p className="text-sm text-gray-500">{data.active === 1 ? 'customer' : 'customers'} right now</p>
          </div>
        </div>
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${data.active > 0 ? 'bg-[#0FA49C]/10' : 'bg-gray-100'}`}>
          <Users className={`w-7 h-7 ${data.active > 0 ? 'text-[#0FA49C]' : 'text-gray-400'}`} />
        </div>
      </div>
      {data.active > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="font-bold text-blue-600">{data.by_step.welcome}</span>
            <span className="text-gray-500">welcome</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="font-bold text-amber-600">{data.by_step.menu}</span>
            <span className="text-gray-500">browsing menu</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <span className="font-bold text-emerald-600">{data.by_step.review}</span>
            <span className="text-gray-500">at checkout</span>
          </span>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Open/Closed toggle
// ============================================================================

const OpenClosedControl = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingMsg, setEditingMsg] = useState(false);
  const [draftMsg, setDraftMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/teds-table/settings`).then(res => {
      setSettings(res.data);
      setDraftMsg(res.data.closed_message || '');
    });
  }, []);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/teds-table/settings`, payload, { headers: authHeaders() });
      setSettings(res.data);
      setDraftMsg(res.data.closed_message || '');
      toast.success(res.data.is_open ? "Ted's Table is now OPEN" : "Ted's Table is now CLOSED");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setSaving(false);
    }
  };

  const saveMessage = async () => {
    if (draftMsg.trim() === (settings?.closed_message || '')) {
      setEditingMsg(false);
      return;
    }
    await patch({ closed_message: draftMsg.trim() });
    setEditingMsg(false);
  };

  if (!settings) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Service Status</p>
          <p className="text-lg font-black mt-0.5">
            {settings.is_open ? (
              <span className="text-emerald-600">● Open — accepting orders</span>
            ) : (
              <span className="text-red-500">● Closed — orders blocked</span>
            )}
          </p>
        </div>
        <button
          onClick={() => patch({ is_open: !settings.is_open })}
          disabled={saving}
          className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
            settings.is_open ? 'bg-emerald-500' : 'bg-gray-300'
          } disabled:opacity-50`}
          data-testid="teds-table-open-toggle"
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow ${
            settings.is_open ? 'translate-x-9' : 'translate-x-1'
          }`} />
        </button>
      </div>

      <div className="border-t border-gray-100 pt-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#666]">Closed message (shown to customers)</p>
          {!editingMsg && (
            <button
              onClick={() => setEditingMsg(true)}
              className="text-xs text-[#0FA49C] font-semibold hover:underline"
              data-testid="teds-table-edit-closed-msg"
            >
              Edit
            </button>
          )}
        </div>
        {editingMsg ? (
          <div className="space-y-2">
            <textarea
              value={draftMsg}
              onChange={(e) => setDraftMsg(e.target.value)}
              rows={3}
              className="w-full text-sm p-2 border border-gray-300 rounded"
              data-testid="teds-table-closed-msg-input"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveMessage} disabled={saving} className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white">Save</Button>
              <Button size="sm" variant="outline" onClick={() => { setEditingMsg(false); setDraftMsg(settings.closed_message || ''); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600 italic">&ldquo;{settings.closed_message}&rdquo;</p>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// Live orders summary tile (top row) — poll-independent counts
// ============================================================================

const LiveOrdersTile = () => {
  const [counts, setCounts] = useState({ new: 0, preparing: 0, delivered: 0 });

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/admin/teds-table/orders`, { headers: authHeaders() });
        if (cancelled) return;
        const paid = (res.data || []).filter(o => o.payment_status === 'paid');
        setCounts({
          new: paid.filter(o => o.status === 'new').length,
          preparing: paid.filter(o => o.status === 'preparing').length,
          delivered: paid.filter(o => o.status === 'delivered').length,
        });
      } catch { /* silent */ }
    };
    fetchIt();
    const t = setInterval(fetchIt, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const active = counts.new + counts.preparing;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="teds-table-live-orders-tile">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Live orders</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-4xl font-black text-[#0FA49C]" data-testid="teds-table-live-orders-count">{active}</p>
            <p className="text-sm text-gray-500">active</p>
          </div>
        </div>
        <div className={`w-14 h-14 rounded-full flex items-center justify-center ${counts.new > 0 ? 'bg-red-500/10 animate-pulse' : active > 0 ? 'bg-amber-500/10' : 'bg-gray-100'}`}>
          <Clock className={`w-7 h-7 ${counts.new > 0 ? 'text-red-500' : active > 0 ? 'text-amber-500' : 'text-gray-400'}`} />
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="font-bold text-red-600">{counts.new}</span>
          <span className="text-gray-500">new</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          <span className="font-bold text-amber-600">{counts.preparing}</span>
          <span className="text-gray-500">preparing</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="font-bold text-emerald-600">{counts.delivered}</span>
          <span className="text-gray-500">delivered</span>
        </span>
      </div>
    </div>
  );
};


// ============================================================================
// Revenue stats panel (today + all-time)
// ============================================================================

const StatCell = ({ label, value, sub }) => (
  <div className="flex-1 min-w-0">
    <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">{label}</p>
    <p className="text-xl sm:text-2xl font-black text-[#1a1a1a] mt-0.5 leading-none">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{sub}</p>
  </div>
);

const StatsPanel = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/admin/teds-table/stats`, { headers: authHeaders() });
        if (!cancelled) setStats(res.data);
      } catch { /* silent */ }
    };
    fetchIt();
    const t = setInterval(fetchIt, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const fmt = (n) => `£${(n || 0).toFixed(2)}`;

  return (
    <div className="bg-gradient-to-r from-[#fafcfb] to-white rounded-xl border border-gray-200 p-4" data-testid="teds-table-stats-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Revenue</p>
        {stats && (
          <p className="text-[10px] text-gray-400">Updates every 15s · UTC midnight cutoff</p>
        )}
      </div>
      {!stats ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 divide-x divide-gray-100">
          <StatCell
            label="Today · orders"
            value={stats.today.count}
            sub={stats.today.count === 1 ? 'order today' : 'orders today'}
          />
          <div className="pl-4">
            <StatCell label="Today · value" value={fmt(stats.today.total)} sub="paid so far today" />
          </div>
          <div className="pl-4">
            <StatCell
              label="All-time · orders"
              value={stats.all_time.count}
              sub={stats.all_time.count === 1 ? 'lifetime order' : 'lifetime orders'}
            />
          </div>
          <div className="pl-4">
            <StatCell label="All-time · value" value={fmt(stats.all_time.total)} sub="paid lifetime" />
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Funnel graph (7-day welcome → menu → review → paid conversion)
// ============================================================================

const FunnelGraph = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const res = await axios.get(`${API}/admin/teds-table/funnel?days=7`, { headers: authHeaders() });
        if (!cancelled) setData(res.data || []);
      } catch { /* silent */ }
    };
    fetchIt();
    const t = setInterval(fetchIt, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-40">
        <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    );
  }

  const totals = data.reduce(
    (acc, d) => ({
      welcome: acc.welcome + d.welcome,
      menu: acc.menu + d.menu,
      review: acc.review + d.review,
      paid: acc.paid + d.paid,
    }),
    { welcome: 0, menu: 0, review: 0, paid: 0 }
  );

  // Max value across all bars, for the chart Y-axis
  const maxCount = Math.max(1, ...data.map(d => Math.max(d.welcome, d.menu, d.review, d.paid)));

  const conv = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
  const scanToPay = conv(totals.paid, totals.welcome);
  const scanToMenu = conv(totals.menu, totals.welcome);

  const dayLabel = (iso) => {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="teds-table-funnel-graph">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Scan → Pay Funnel · Last 7 days</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Unique customers per step per day</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span>
            <span className="text-gray-500">Scan → Menu </span>
            <strong className="text-[#0FA49C]">{scanToMenu}%</strong>
          </span>
          <span>
            <span className="text-gray-500">Scan → Pay </span>
            <strong className="text-emerald-600">{scanToPay}%</strong>
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="flex items-end gap-2 h-40 pb-2 border-b border-gray-100" data-testid="teds-table-funnel-chart">
        {data.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
            <div className="flex items-end gap-0.5 h-32 w-full justify-center">
              {[
                { key: 'welcome', color: 'bg-blue-400', label: 'Welcome' },
                { key: 'menu', color: 'bg-amber-400', label: 'Menu' },
                { key: 'review', color: 'bg-orange-500', label: 'Review' },
                { key: 'paid', color: 'bg-emerald-500', label: 'Paid' },
              ].map((bar) => {
                const value = d[bar.key];
                const heightPct = value > 0 ? Math.max(4, (value / maxCount) * 100) : 2;
                return (
                  <div
                    key={bar.key}
                    className={`${bar.color} rounded-t w-full max-w-[10px] transition-all duration-500 relative group`}
                    style={{ height: `${heightPct}%` }}
                    title={`${bar.label}: ${value}`}
                  >
                    {value > 0 && (
                      <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-bold text-gray-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                        {value}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-500 font-semibold mt-1 truncate w-full text-center">
              {dayLabel(d.date)}
            </p>
          </div>
        ))}
      </div>

      {/* Legend + Totals */}
      <div className="mt-4 grid grid-cols-4 gap-2 text-center">
        {[
          { key: 'welcome', color: 'bg-blue-400', label: 'Scanned QR' },
          { key: 'menu', color: 'bg-amber-400', label: 'Opened menu' },
          { key: 'review', color: 'bg-orange-500', label: 'Reached checkout' },
          { key: 'paid', color: 'bg-emerald-500', label: 'Paid' },
        ].map((b) => (
          <div key={b.key} className="min-w-0">
            <div className="flex items-center justify-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-sm ${b.color}`} />
              <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold truncate">{b.label}</span>
            </div>
            <p className="text-xl font-black text-[#1a1a1a] mt-0.5" data-testid={`teds-table-funnel-total-${b.key}`}>{totals[b.key]}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


// ============================================================================
// Promo banner editor — controls the persistent footer banner shown on
// welcome + menu screens. Colours default to brand green/lime.
// ============================================================================

const DEFAULT_BANNER = {
  enabled: true,
  text: "\uD83D\uDC3E Join Ted's Table today and get 10% OFF your first order!",
  url: '/signup',
  bg_color: '#1f6d62',
  text_color: '#ffffff',
  accent_color: '#a0da39',
};

const BannerEditor = () => {
  const [banner, setBanner] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/teds-table/settings`).then(res => {
      setBanner({ ...DEFAULT_BANNER, ...(res.data.banner || {}) });
    });
  }, []);

  const patch = (patchObj) => {
    setBanner(prev => ({ ...prev, ...patchObj }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/teds-table/settings`, { banner }, { headers: authHeaders() });
      setBanner({ ...DEFAULT_BANNER, ...(res.data.banner || {}) });
      setDirty(false);
      toast.success('Banner updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save banner');
    } finally {
      setSaving(false);
    }
  };

  if (!banner) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3" data-testid="teds-table-banner-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-[#0FA49C]" />
          <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Promo Banner (footer)</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-xs text-gray-500">{banner.enabled ? 'On' : 'Off'}</span>
          <button
            type="button"
            onClick={() => patch({ enabled: !banner.enabled })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              banner.enabled ? 'bg-emerald-500' : 'bg-gray-300'
            }`}
            data-testid="teds-table-banner-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
              banner.enabled ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </label>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-700 mb-1 block">Banner text</label>
        <Input
          value={banner.text || ''}
          onChange={(e) => patch({ text: e.target.value })}
          placeholder="e.g. 🐾 Join Ted's Table today and get 10% OFF!"
          data-testid="teds-table-banner-text-input"
        />
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-700 mb-1 block">Link URL (internal path or full URL)</label>
        <Input
          value={banner.url || ''}
          onChange={(e) => patch({ url: e.target.value })}
          placeholder="/signup or https://..."
          data-testid="teds-table-banner-url-input"
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { key: 'bg_color', label: 'Background' },
          { key: 'text_color', label: 'Text' },
          { key: 'accent_color', label: 'Accent' },
        ].map(f => (
          <div key={f.key}>
            <label className="text-[10px] font-semibold text-gray-500 mb-1 block uppercase tracking-wider">{f.label}</label>
            <div className="flex items-center gap-1.5">
              <input
                type="color"
                value={banner[f.key] || '#000000'}
                onChange={(e) => patch({ [f.key]: e.target.value })}
                className="w-8 h-8 border border-gray-200 rounded cursor-pointer"
                data-testid={`teds-table-banner-${f.key}`}
              />
              <input
                type="text"
                value={banner[f.key] || ''}
                onChange={(e) => patch({ [f.key]: e.target.value })}
                className="flex-1 min-w-0 text-xs px-2 py-1 border border-gray-200 rounded font-mono"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Live preview */}
      <div>
        <p className="text-[10px] font-semibold text-gray-500 mb-1 uppercase tracking-wider">Preview</p>
        <div
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm text-center"
          style={{
            backgroundColor: banner.bg_color,
            color: banner.text_color,
            borderTop: `2px solid ${banner.accent_color}`,
          }}
          data-testid="teds-table-banner-preview"
        >
          <span>{banner.text || 'Banner preview'}</span>
        </div>
      </div>

      {dirty && (
        <Button
          onClick={save}
          disabled={saving}
          size="sm"
          className="bg-[#0FA49C] hover:bg-[#0d8f88] text-white w-full"
          data-testid="teds-table-banner-save"
        >
          {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : 'Save banner'}
        </Button>
      )}
    </div>
  );
};


// ============================================================================
// Closed-state signups — customers who scanned the QR while shop was closed
// ============================================================================

const ClosedSignups = () => {
  const [data, setData] = useState(null);

  const fetchIt = async () => {
    try {
      const res = await axios.get(`${API}/admin/teds-table/closed-stats`, { headers: authHeaders() });
      setData(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchIt();
    const t = setInterval(fetchIt, 30000);
    return () => clearInterval(t);
  }, []);

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/teds-table/closed-signups/${encodeURIComponent(email)}`, { headers: authHeaders() });
      toast.success('Removed');
      fetchIt();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  const copyAll = () => {
    if (!data?.signups?.length) return;
    const list = data.signups.map(s => s.email).join(', ');
    navigator.clipboard.writeText(list);
    toast.success(`Copied ${data.signups.length} emails`);
  };

  if (!data) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid="teds-table-closed-signups">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#0FA49C]" />
            <p className="text-xs uppercase tracking-widest text-[#666] font-bold">Closed-hours interest</p>
          </div>
          <p className="text-[10px] text-gray-400 mt-0.5">Customers who scanned while shop was closed</p>
        </div>
        {data.signups?.length > 0 && (
          <Button size="sm" variant="outline" onClick={copyAll} data-testid="teds-table-closed-copy">
            Copy emails
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Scans</p>
          <p className="text-2xl font-black text-[#1a1a1a] mt-0.5" data-testid="teds-table-closed-scans">{data.scans_while_closed || 0}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2.5 text-center">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Signups</p>
          <p className="text-2xl font-black text-[#1a1a1a] mt-0.5" data-testid="teds-table-closed-signup-count">{data.signup_count || 0}</p>
        </div>
      </div>
      {data.signups?.length > 0 ? (
        <div className="max-h-56 overflow-y-auto border-t border-gray-100 -mx-4 px-4">
          {data.signups.map(s => (
            <div key={s.email} className="flex items-center justify-between py-2 border-b border-gray-50 text-sm">
              <div className="min-w-0">
                <p className="font-semibold text-[#1a1a1a] truncate">{s.email}</p>
                {s.name && <p className="text-xs text-gray-500 truncate">{s.name}</p>}
              </div>
              <button
                onClick={() => remove(s.email)}
                className="text-gray-400 hover:text-red-500 p-1"
                data-testid={`teds-table-closed-remove-${s.email}`}
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400 italic text-center py-3">No closed-hours signups yet.</p>
      )}
    </div>
  );
};


// ============================================================================
// Main tab
// ============================================================================

export const TedsTableAdmin = () => {
  const [subTab, setSubTab] = useState('orders');

  const publicUrl = `${window.location.origin}/teds-table`;

  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-[#1f6d62] to-[#14544b] text-white rounded-xl p-5 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/70">TED&apos;S TABLE</p>
            <h2 className="text-2xl font-black mt-0.5">In-Store Ordering</h2>
            <p className="text-sm text-white/80 mt-1">Powered by Ultimate Pet Foods · FFos Caerffili</p>
          </div>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 bg-[#a0da39] hover:bg-[#8bc620] text-[#1a1a1a] px-4 py-2.5 rounded-lg text-sm font-bold transition-colors"
            data-testid="teds-table-public-link"
          >
            <ExternalLink className="w-4 h-4" />
            Open customer view
          </a>
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/70 font-mono">
          QR link: {publicUrl}
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setSubTab('orders')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'orders' ? 'border-[#0FA49C] text-[#0FA49C]' : 'border-transparent text-[#666] hover:text-[#222]'
          }`}
          data-testid="teds-table-sub-orders"
        >
          <Clock className="w-4 h-4 inline mr-1.5" /> Live Orders
        </button>
        <button
          onClick={() => setSubTab('menu')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            subTab === 'menu' ? 'border-[#0FA49C] text-[#0FA49C]' : 'border-transparent text-[#666] hover:text-[#222]'
          }`}
          data-testid="teds-table-sub-menu"
        >
          <Utensils className="w-4 h-4 inline mr-1.5" /> Menu
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <OpenClosedControl />
        <PresenceCounter />
        <LiveOrdersTile />
      </div>

      <StatsPanel />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BannerEditor />
        <ClosedSignups />
      </div>

      <FunnelGraph />

      {subTab === 'orders' ? <OrdersView /> : <MenuEditor />}
    </div>
  );
};
